import { useCallback, useMemo } from 'react';

import {
  muteChatRoomAgent,
  resumeChatRoomAgent,
  submitAgentFeedback,
  useInvalidateChatRooms,
} from '@commenda/api';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { ChatRoomAgentFeedbackRating } from '@commenda/types';

import { useNotification } from '../../hooks/useNotification';
import type {
  AgentReplyActions,
  UseThreadAgentActionsParams,
} from '../ChatRoom/types';

export function useThreadAgentActions({
  chatRoomId,
  channel,
  isAgentMuted,
}: Readonly<UseThreadAgentActionsParams>): AgentReplyActions {
  const { setNotification } = useNotification();
  const invalidateChatRooms = useInvalidateChatRooms();

  const showActionError = useCallback(
    (title: string, error: unknown) =>
      setNotification({
        show: true,
        valid: false,
        title,
        message: error instanceof Error ? error.message : 'Please try again.',
      }),
    [setNotification]
  );

  const onAgentReplyRate = useCallback(
    async (messageId: string, rating: ChatRoomAgentFeedbackRating) => {
      if (!chatRoomId) return false;
      try {
        await submitAgentFeedback(chatRoomId, messageId, rating);
      } catch (error) {
        showActionError('Feedback not saved', error);
        return false;
      }
      mixpanel.track(MIXPANEL_EVENTS.THREAD_AGENT_FEEDBACK_GIVEN, {
        surface_area: MIXPANEL_SURFACE_AREA.THREADS,
        chat_room_id: chatRoomId,
        channel,
        message_id: messageId,
        rating,
      });
      invalidateChatRooms(chatRoomId);
      return true;
    },
    [chatRoomId, channel, invalidateChatRooms, showActionError]
  );

  const onAgentMuteChange = useCallback(
    async (isMuted: boolean) => {
      if (!chatRoomId) return false;
      const updateAgentMute = isMuted ? muteChatRoomAgent : resumeChatRoomAgent;
      try {
        await updateAgentMute(chatRoomId, channel);
      } catch (error) {
        showActionError(
          isMuted ? 'Commenda Agent not muted' : 'Commenda Agent not resumed',
          error
        );
        return false;
      }
      mixpanel.track(
        isMuted
          ? MIXPANEL_EVENTS.THREAD_AGENT_MUTED
          : MIXPANEL_EVENTS.THREAD_AGENT_RESUMED,
        {
          surface_area: MIXPANEL_SURFACE_AREA.THREADS,
          chat_room_id: chatRoomId,
          channel,
        }
      );
      invalidateChatRooms(chatRoomId);
      return true;
    },
    [chatRoomId, channel, invalidateChatRooms, showActionError]
  );

  return useMemo(
    () => ({ isAgentMuted, onAgentReplyRate, onAgentMuteChange }),
    [isAgentMuted, onAgentReplyRate, onAgentMuteChange]
  );
}
